import { App, Button, Descriptions, Divider, Flex, Input, InputNumber, List, Spin } from "antd";
import isEqual from "lodash/isEqual";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CompanyContext, UpdateCompanyContext } from "../../../../context/companyContext";
import { clientFetch } from "../../../../utils/clientFetch";
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../../util/helper";
import {
  CompanyItemMasterFieldError,
  CompanyItemMasterFormValue,
  CompanyItemMasterResponseType,
  createDefaultCompanyItemMasterForm,
  mapCompanyItemMasterResponseToForm,
  toCompanyItemMasterRequestBody,
  validateCompanyItemMasterForm,
} from "../itemMasterHelper";

type ItemMasterFieldDefinition = {
  key: keyof CompanyItemMasterFormValue;
  labelKey: string;
  type: "text" | "number";
  min?: number;
  max?: number;
  required?: boolean;
};

type ApiErrorPayload = {
  message?: string;
};

const FIELD_DEFINITIONS: ItemMasterFieldDefinition[] = [
  { key: "jan_code", labelKey: "itemMaster.janCode", type: "text", required: true },
  { key: "repeat", labelKey: "itemMaster.repeat", type: "number", min: 0 },
  { key: "part_number", labelKey: "itemMaster.partNumber", type: "text", required: true },
  { key: "sample_number", labelKey: "itemMaster.sampleNumber", type: "text" },
  { key: "item_name", labelKey: "itemMaster.itemName", type: "text", required: true },
  { key: "brand_name", labelKey: "itemMaster.brandName", type: "text", required: true },
  { key: "size", labelKey: "itemMaster.size", type: "text" },
  { key: "color_no", labelKey: "itemMaster.colorNo", type: "text", required: true },
];

export function CompanyItemMasterManage() {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { company } = useContext(CompanyContext);
  const { refreshCompany } = useContext(UpdateCompanyContext);
  const { companyId, itemMasterId } = useParams();
  const navigate = useNavigate();

  const [itemMasterInfo, setItemMasterInfo] = useState<CompanyItemMasterFormValue>(createDefaultCompanyItemMasterForm());
  const [originalItemMasterInfo, setOriginalItemMasterInfo] = useState<CompanyItemMasterFormValue>(createDefaultCompanyItemMasterForm());
  const [errors, setErrors] = useState<CompanyItemMasterFieldError>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);

  const isRegisterMode = itemMasterId === "register";
  const isUnchanged = !isRegisterMode && isEqual(itemMasterInfo, originalItemMasterInfo);
  const pageTitle = isRegisterMode ? t("itemMaster.registerPage") : t("itemMaster.editPage");

  const getItemMasterListPath = useCallback((targetCompanyId: string) => {
    const params = new URLSearchParams({ companyId: targetCompanyId });
    return `${PATH_INFO.COMPANY_ITEM_MASTER.PATH}?${params.toString()}`;
  }, []);

  const onClickBack = useCallback(() => {
    if (!companyId) {
      navigate(-1);
      return;
    }

    navigate(getItemMasterListPath(companyId));
  }, [companyId, getItemMasterListPath, navigate]);

  const parseApiErrorMessage = useCallback(async (response: Response) => {
    try {
      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) {
        return undefined;
      }

      const payload = await response.clone().json() as ApiErrorPayload;
      if (typeof payload.message === "string" && payload.message.trim() !== "") {
        return payload.message;
      }
    } catch (error) {
      console.error("Failed to parse item master manage error response.", error);
    }

    return undefined;
  }, []);

  const updateField = useCallback(
    <K extends keyof CompanyItemMasterFormValue>(key: K, value: CompanyItemMasterFormValue[K]) => {
      setItemMasterInfo((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => ({ ...prev, [key]: false }));
    },
    []
  );

  const fetchItemMaster = useCallback(async (id: string) => {
    const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/company-item-masters/${id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch company item master");
    }

    const payload = await response.json() as CompanyItemMasterResponseType;
    return payload;
  }, []);

  useEffect(() => {
    let isCancelled = false;

    setItemMasterInfo(createDefaultCompanyItemMasterForm());
    setOriginalItemMasterInfo(createDefaultCompanyItemMasterForm());
    setErrors({});
    setIsPageReady(false);

    const validateAndLoad = async () => {
      if (!companyId || !itemMasterId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
        return;
      }

      if (!isRegisterMode && !/^\d+$/.test(itemMasterId)) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
        return;
      }

      setIsLoading(true);

      try {
        const companyResult = await refreshCompany(companyId);

        if (isCancelled) {
          return;
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return;
        }

        if (isRegisterMode) {
          const initialValue = createDefaultCompanyItemMasterForm();
          setItemMasterInfo(initialValue);
          setOriginalItemMasterInfo(initialValue);
          setIsPageReady(true);
          return;
        }

        const itemMaster = await fetchItemMaster(itemMasterId);

        if (isCancelled) {
          return;
        }

        if (itemMaster.company?.id !== Number(companyId)) {
          navigate(PATH_INFO.ERROR.PATH, { replace: true });
          return;
        }

        const mapped = mapCompanyItemMasterResponseToForm(itemMaster);
        setItemMasterInfo(mapped);
        setOriginalItemMasterInfo(mapped);
        setIsPageReady(true);
      } catch (error) {
        console.error("Error validating company item master manage.", error);
        if (isCancelled) {
          return;
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    validateAndLoad();

    return () => {
      isCancelled = true;
    };
  }, [companyId, fetchItemMaster, isRegisterMode, itemMasterId, navigate, refreshCompany]);

  const onClickSubmit = useCallback(async () => {
    if (!companyId || !itemMasterId) {
      return;
    }

    const validationResult = validateCompanyItemMasterForm(itemMasterInfo);
    if (!validationResult.isValid) {
      setErrors(validationResult.errors);
      modal.error({
        content: validationResult.messageKey
          ? t(validationResult.messageKey, validationResult.messageValues)
          : t("messages.confirmInput"),
        centered: true,
      });
      return;
    }

    if (!isRegisterMode && isUnchanged) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const requestBody = toCompanyItemMasterRequestBody(itemMasterInfo);
      const endpoint = isRegisterMode
        ? `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/company-item-masters`
        : `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/company-item-masters/${itemMasterId}`;
      const response = await clientFetch(endpoint, {
        method: isRegisterMode ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isRegisterMode
            ? { ...requestBody, company_id: Number(companyId) }
            : requestBody
        ),
      });

      if (!response.ok) {
        const errorMessage = await parseApiErrorMessage(response);
        const duplicateMessage = response.status === 409 ? t("itemMaster.messages.duplicateJanCode") : undefined;
        const validationMessage = response.status === 400 ? t("messages.confirmInput") : undefined;
        const notFoundMessage = response.status === 404 ? t("itemMaster.messages.notFound") : undefined;
        modal.error({
          content: duplicateMessage
            ?? validationMessage
            ?? notFoundMessage
            ?? errorMessage
            ?? (isRegisterMode ? t("itemMaster.messages.registerFailed") : t("itemMaster.messages.updateFailed")),
          centered: true,
        });
        return;
      }

      modal.success({
        content: isRegisterMode ? t("itemMaster.messages.registerSucceeded") : t("itemMaster.messages.updateSucceeded"),
        centered: true,
        onOk() {
          navigate(getItemMasterListPath(companyId));
        },
      });
    } catch (error) {
      console.error("Error saving company item master.", error);
      modal.error({
        content: t("messages.processFailedRetry"),
        centered: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [companyId, getItemMasterListPath, isRegisterMode, isUnchanged, itemMasterId, itemMasterInfo, modal, navigate, parseApiErrorMessage, t]);

  const inputPlaceHolder = useMemo(() => ({
    title: company?.company_name ?? t("itemMaster.title"),
    column: FIELD_DEFINITIONS,
  }), [company?.company_name, t]);

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t("common.loadingWithDots")} />
      </Flex>
    );
  }

  return (
    <div className="relative">
      <Spin spinning={isLoading} tip={t("common.processing")} size="large" fullscreen />
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2 break-all">
          {pageTitle}
        </p>
      </div>
      <Divider />
      <Flex vertical align="center">
        <List
          header={<div className="font-bold break-all">{inputPlaceHolder.title}</div>}
          bordered
          dataSource={inputPlaceHolder.column}
          renderItem={(field) => (
            <List.Item style={{ background: "#FFFFFF", justifyContent: "end" }}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item
                  label={(
                    <span className="relative inline-block pr-2">
                      {field.required && <span className="absolute right-0 top-0 text-red-500 text-[8px] leading-none">※</span>}
                      <span>{t(field.labelKey)}</span>
                    </span>
                  )}
                  key={field.key}
                >
                  {field.type === "text" && (
                    <Input
                      value={itemMasterInfo[field.key] ?? ""}
                      onChange={(event) => updateField(field.key, event.target.value as CompanyItemMasterFormValue[typeof field.key])}
                      className="w-[320px]"
                      status={errors[field.key] ? "error" : ""}
                    />
                  )}
                  {field.type === "number" && (
                    <InputNumber<number>
                      className="w-[320px]"
                      min={field.min}
                      max={field.max}
                      precision={0}
                      value={typeof itemMasterInfo[field.key] === "number" ? itemMasterInfo[field.key] as number : null}
                      onChange={(value) => updateField(field.key, (value ?? null) as CompanyItemMasterFormValue[typeof field.key])}
                      status={errors[field.key] ? "error" : ""}
                    />
                  )}
                </Descriptions.Item>
              </Descriptions>
            </List.Item>
          )}
          className="overflow-hidden bg-[#FAFAFA] text-center"
        />
        <Flex gap="large" className="mt-5">
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack} disabled={isLoading}>{t("common.back")}</Button>
          <Button type="primary" size="large" onClick={onClickSubmit} loading={isLoading} disabled={isLoading || (!isRegisterMode && isUnchanged)}>
            {isRegisterMode ? t("common.register") : t("common.update")}
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
